import React, { useState, useEffect, useCallback, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { formatCoordinates, copyToClipboard, FormattedCoordinates } from '../utils/coordinateFormatters';
import { GetFeatureInfoCoordinator } from './services/GetFeatureInfoCoordinator';
import { SpotWxHandler } from './mapcontrols/SpotWxControl/SpotWxHandler';
import { PlacenameHandler } from './mapcontrols/PlacenameTool/PlacenameHandler';
import { usePermissions } from '../context/PermissionContext';

/** Layers that have their own context menu — don't show the general one when clicking these */
const FEATURE_CONTEXT_MENU_LAYERS = ['current-fires-layer', 'last-fires-layer'];
/** Prefix for DB-driven layers with contextMenu config — also suppressed */
const FEATURE_CONTEXT_MENU_PREFIX = 'live-geojson-layer-';

const LONG_PRESS_MS = 500;

interface ContextMenuState {
  lngLat: mapboxgl.LngLat;
  pixel: { x: number; y: number };
}

interface MapContextMenuProps {
  map: mapboxgl.Map;
  coordinator: GetFeatureInfoCoordinator | null;
  spotWxHandler: SpotWxHandler | null;
  placenameHandler: PlacenameHandler | null;
}

const MapContextMenu: React.FC<MapContextMenuProps> = ({ map, coordinator, spotWxHandler, placenameHandler }) => {
  const { canDo } = usePermissions();
  const canCreateVar = canDo('var', 'create');
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  /** Check if any feature-specific context menu layer has a feature at this point */
  const hasFeatureAtPoint = useCallback((point: mapboxgl.PointLike): boolean => {
    // Check static layer IDs
    const activeLayers = FEATURE_CONTEXT_MENU_LAYERS.filter(id => {
      try { return !!map.getLayer(id); } catch { return false; }
    });
    // Check DB-driven live-geojson layers with contextMenu config
    const style = map.getStyle();
    if (style?.layers) {
      for (const layer of style.layers) {
        if (layer.id.startsWith(FEATURE_CONTEXT_MENU_PREFIX) && !activeLayers.includes(layer.id)) {
          activeLayers.push(layer.id);
        }
      }
    }
    if (activeLayers.length === 0) return false;
    const features = map.queryRenderedFeatures(point, { layers: activeLayers });
    return features.length > 0;
  }, [map]);

  const openMenu = useCallback((lngLat: mapboxgl.LngLat, point: mapboxgl.PointLike) => {
    if (hasFeatureAtPoint(point)) return;
    const projected = map.project(lngLat);
    setMenu({ lngLat, pixel: { x: projected.x, y: projected.y } });
  }, [map, hasFeatureAtPoint]);

  useEffect(() => {
    // Right-click (desktop)
    const handleContextMenu = (e: mapboxgl.MapMouseEvent) => {
      e.preventDefault();
      openMenu(e.lngLat, e.point);
    };

    // Long-press (mobile)
    const handleTouchStart = (e: mapboxgl.MapTouchEvent) => {
      if (e.originalEvent.touches.length !== 1) return;
      const touch = e.originalEvent.touches[0];
      touchStart.current = { x: touch.clientX, y: touch.clientY };
      longPressTimer.current = setTimeout(() => {
        openMenu(e.lngLat, e.point);
      }, LONG_PRESS_MS);
    };

    const handleTouchMove = (e: mapboxgl.MapTouchEvent) => {
      if (!touchStart.current || !longPressTimer.current) return;
      const touch = e.originalEvent.touches[0];
      const dx = touch.clientX - touchStart.current.x;
      const dy = touch.clientY - touchStart.current.y;
      // Cancel if finger moved more than 10px (it's a pan, not a long-press)
      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };

    const handleTouchEnd = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };

    // Dismiss on map interaction
    const handleMapMove = () => setMenu(null);
    const handleClick = () => setMenu(null);

    map.on('contextmenu', handleContextMenu);
    map.on('touchstart', handleTouchStart);
    map.on('touchmove', handleTouchMove);
    map.on('touchend', handleTouchEnd);
    map.on('movestart', handleMapMove);
    map.on('click', handleClick);

    return () => {
      map.off('contextmenu', handleContextMenu);
      map.off('touchstart', handleTouchStart);
      map.off('touchmove', handleTouchMove);
      map.off('touchend', handleTouchEnd);
      map.off('movestart', handleMapMove);
      map.off('click', handleClick);
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, [map, openMenu]);

  // Dismiss on Escape key
  useEffect(() => {
    if (!menu) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menu, closeMenu]);

  const handleCopy = async (format: keyof FormattedCoordinates, value: string) => {
    const success = await copyToClipboard(value);
    if (success) {
      closeMenu();
    }
  };

  const handleGetInfo = () => {
    if (coordinator && menu) {
      const point = map.project(menu.lngLat);
      coordinator.queryAt(menu.lngLat, point);
    }
    closeMenu();
  };

  const handleSpotWx = () => {
    if (spotWxHandler && menu) {
      spotWxHandler.showForecast(menu.lngLat);
    }
    closeMenu();
  };

  const handlePlacenameLookup = () => {
    if (placenameHandler && menu) {
      placenameHandler.lookup(menu.lngLat);
    }
    closeMenu();
  };

  const handlePlacenameSearch = () => {
    if (placenameHandler && menu) {
      placenameHandler.showSearchForm(menu.lngLat);
    }
    closeMenu();
  };

  const handleAddVAR = () => {
    if (menu) {
      window.dispatchEvent(new CustomEvent('intellifire:var-create', {
        detail: { lat: menu.lngLat.lat, lng: menu.lngLat.lng }
      }));
    }
    closeMenu();
  };

  if (!menu) return null;

  const { lat, lng } = menu.lngLat;
  const coords = formatCoordinates(lat, lng);

  const coordRows: { label: string; key: keyof FormattedCoordinates }[] = [
    { label: 'DDM', key: 'dmm' },
    { label: 'DD', key: 'dd' },
    { label: 'DMS', key: 'dms' },
  ];

  return (
    <div
      className="context-menu"
      style={{
        position: 'absolute',
        left: `${menu.pixel.x}px`,
        top: `${menu.pixel.y}px`,
        pointerEvents: 'auto',
      }}
    >
      <h3 className="context-menu__header">Map Location</h3>
      <div className="context-menu__coords">
        {coordRows.map(({ label, key }) => (
          <div key={key} className="context-menu__coord-row">
            <span className="context-menu__coord-label">{label}:</span>
            <span className="context-menu__coord-value">{coords[key]}</span>
            <button
              className="context-menu__coord-copy"
              onClick={() => handleCopy(key, coords[key])}
              title={`Copy ${label} coordinates`}
            >
              ⧉
            </button>
          </div>
        ))}
      </div>
      <div className="context-menu__divider" />
      <button className="context-menu__action" onClick={handleGetInfo}>
        Get Info
      </button>
      <button className="context-menu__action" onClick={handleSpotWx}>
        SpotWX Forecast Here
      </button>
      <button className="context-menu__action" onClick={handlePlacenameLookup}>
        Placenames Nearby (5km)
      </button>
      <button className="context-menu__action" onClick={handlePlacenameSearch}>
        Search Placenames (Prov/Terr)
      </button>
      {canCreateVar && (
        <>
          <div className="context-menu__divider" />
          <button className="context-menu__action" onClick={handleAddVAR}>
            Add New VAR
          </button>
        </>
      )}
    </div>
  );
};

export default MapContextMenu;
